import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    SimpleGrid,
    Text,
    Flex,
    Box,
    Divider,
    useToast,
    Spinner,
    VStack,
    HStack,
    Select,
    Badge,
    Input,
    FormControl,
    FormLabel,
    Textarea,
} from "@chakra-ui/react";

import ApplicationsApi, { Applications } from "../../../../api/applications";
import React, { useEffect, useState } from "react";
import { prettifyTransferDate } from "../../../../functions/dateUtils";

export default function TransferDetailsModal(props) {
    let { isOpen, onClose, application } = props;
    const toast = useToast();
    const [applicationDetails, setApplicationDetails] = useState(null);
    const [transferLog, setTransferLog] = useState([]);
    const [loading, setLoading] = useState(false);
    const [applicationStatus, setApplicationStatus] = useState("");
    const [bankReference, setBankReference] = useState("");
    const [bankActivityReference, setBankActivityReference] = useState("");
    const [showBankInputs, setShowBankInputs] = useState(false);

    const [notes, setNotes] = useState("");
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [originalNotes, setOriginalNotes] = useState("");

    // Get isEditable flag from application details (set by backend based on user role)
    const isEditable = applicationDetails?.isEditable !== false;

    useEffect(() => {
        if (isOpen && application && application.id) {
            fetchApplicationDetails();
        } else if (!isOpen) {
            // Reset state when modal is closed
            setBankReference("");
            setBankActivityReference("");
            setShowBankInputs(false);
            setNotes("");
            setOriginalNotes("");
            setIsEditingNotes(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, application]);

    useEffect(() => {
        if (applicationDetails) {
            let notesDescription = '';
            if (applicationDetails.notes) {
                try {
                    const notesJson = JSON.parse(applicationDetails.notes);
                    notesDescription = notesJson.description || '';
                } catch (e) {
                    notesDescription = applicationDetails.notes;
                }
            }
            setNotes(notesDescription);
            setOriginalNotes(notesDescription);
            setIsEditingNotes(false); // Reset editing state when loading new data
        }
    }, [applicationDetails]);

    const fetchApplicationDetails = async () => {
        setLoading(true);
        try {
            const response = await ApplicationsApi.getApplicationDetails(application.id);
            if (response && response.data) {
                setApplicationDetails(response.data.application);
                setTransferLog(response.data.transferLog || []);
                setApplicationStatus(response.data.application.status.toString());
            }
        } catch (error) {
            toast({
                title: `שגיאה בטעינת פרטי ההעברה`,
                position: 'top',
                status: 'error',
                isClosable: false,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (event) => {
        const value = event.target.value;
        setApplicationStatus(value);

        // Show bank input fields if changing to "transferred" (500) and there are no existing bank references
        const isChangingToTransferred = value === "500";
        const hasExistingBankReferences = applicationDetails?.bankReference || applicationDetails?.bankActivityReference;
        const wasAlreadyTransferred = applicationDetails?.status === 500;

        setShowBankInputs(isChangingToTransferred && !hasExistingBankReferences && !wasAlreadyTransferred);

        // Clear bank input fields and errors when not showing inputs
        if (!isChangingToTransferred || hasExistingBankReferences || wasAlreadyTransferred) {
            setBankReference("");
            setBankActivityReference("");
        }
    };



    const handleUpdateStatus = async () => {
        if (!applicationStatus || applicationStatus === applicationDetails?.status?.toString()) {
            return; // No change needed
        }

        // If bank inputs are required but not filled, don't save
        if (showBankInputs && (!bankReference.trim() || !bankActivityReference.trim())) {
            return;
        }

        try {
            await ApplicationsApi.updateStatus(
                parseInt(applicationStatus),
                [applicationDetails],
                showBankInputs ? bankReference.trim() : null,
                showBankInputs ? bankActivityReference.trim() : null
            );

            toast({
                title: `סטטוס עודכן בהצלחה`,
                position: 'top',
                status: 'success',
                isClosable: false,
            });
            // Refresh the application details to show updated status
            await fetchApplicationDetails();
        } catch (error) {
            toast({
                title: `שגיאה בעדכון סטטוס`,
                position: 'top',
                status: 'error',
                isClosable: false,
            });
        }
    };

    const handleEditNotes = () => {
        setIsEditingNotes(true);
    };

    const handleCancelEdit = () => {
        setNotes(originalNotes); // Restore original notes
        setIsEditingNotes(false);
    };

    const handleSaveNotes = async () => {
        try {
            await ApplicationsApi.updateNotes(applicationDetails.id, notes);
            toast({
                title: `הערות נשמרו בהצלחה`,
                position: 'top',
                status: 'success',
                isClosable: false,
            });
            setOriginalNotes(notes);
            setIsEditingNotes(false);
            await fetchApplicationDetails(); // Refresh to show updated notes
        } catch (error) {
            toast({
                title: `שגיאה בשמירת הערות`,
                position: 'top',
                status: 'error',
                isClosable: false,
            });
        }
    };

    const formatTransferLogAction = (action) => {
        switch (action) {
            case 'PENDING_OTP_CONFIRM':
                return 'קבלת קוד אימות העברה';
            case 'PENDING_TRANSFER':
                return 'אישר העברה';
            case 'PENDING_APPROVAL':
                return 'לפני העברה';
            case 'TRANSFERRED':
                return 'הועבר';
            case 'BANK_REJECTED':
                return 'נדחה על ידי הבנק';
            case 'TRANSFER_RECEIPT':
                return 'אישור העברה';
            case 'COMPLETED':
                return 'הושלם';
            default:
                return action;
        }
    };

    const getStatusBadgeColor = (status) => {
        switch (parseInt(status)) {
            case 0:
                return 'red';
            case 100:
                return 'red';
            case 300:
                return 'yellow';
            case 500:
                return 'blue';
            case 700:
                return 'green';
            default:
                return 'gray';
        }
    };

    return (
        <Modal isOpen={isOpen}
            onClose={onClose}
            isCentered
            closeOnOverlayClick={false}
            size="xl">
            <ModalOverlay />
            <ModalContent maxW="800px">
                <ModalHeader>פרטי העברה</ModalHeader>
                <ModalBody>
                    {loading ? (
                        <Flex justify="center" align="center" height="200px">
                            <Spinner size="lg" />
                        </Flex>
                    ) : (
                        <VStack spacing={6} align="stretch">
                            {/* Basic Transfer Information */}
                            <Box>
                                <Text fontSize="lg" fontWeight="bold" mb={3}>מידע כללי</Text>
                                <SimpleGrid columns={2} spacing={4}>
                                    <Box>
                                        <Text fontWeight="semibold">סכום:</Text>
                                        <Text>₪{application?.amount}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="semibold">סטטוס:</Text>
                                        <Flex align="center" gap={2} mt={1}>
                                            <Select
                                                value={applicationStatus}
                                                onChange={handleStatusChange}
                                                width="200px"
                                                size="sm"
                                                isDisabled={!isEditable}
                                            >
                                                <option value='0'>בוטל</option>
                                                <option value='100'>נדחה</option>
                                                <option value='300'>ממתין להעברה</option>
                                                <option value='500'>הועבר</option>
                                                <option value='700'>הושלם</option>
                                            </Select>
                                            <Badge colorScheme={getStatusBadgeColor(applicationStatus)}>
                                                {Applications.statusText(parseInt(applicationStatus))}
                                            </Badge>
                                        </Flex>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="semibold">תאריך בקשה:</Text>
                                        <Text>{prettifyTransferDate(application?.createdAt)}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="semibold">עמלת העברה:</Text>
                                        <Text>₪{applicationDetails?.transferFee || 0}</Text>
                                    </Box>
                                </SimpleGrid>
                            </Box>

                            {/* Manual Bank Reference Inputs - shown when changing to transferred status (only for editable users) */}
                            {isEditable && showBankInputs && (
                                <>
                                    <Divider />
                                    <Box>
                                        <Text fontSize="lg" fontWeight="bold" mb={3}>פרטי בנק (חובה)</Text>
                                        <SimpleGrid columns={1} spacing={4}>
                                            <FormControl>
                                                <FormLabel>מספר אסמכתא בנק</FormLabel>
                                                <Input
                                                    value={bankReference}
                                                    onChange={(e) => setBankReference(e.target.value)}
                                                    placeholder="הזן מספר אסמכתא בנק"
                                                />
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel>מספר פעילות בנק</FormLabel>
                                                <Input
                                                    value={bankActivityReference}
                                                    onChange={(e) => setBankActivityReference(e.target.value)}
                                                    placeholder="הזן מספר פעילות בנק"
                                                />
                                            </FormControl>
                                        </SimpleGrid>
                                    </Box>
                                </>
                            )}

                            <Divider />

                            {/* Bank References */}
                            {(applicationDetails?.bankReference || applicationDetails?.bankActivityReference) && (
                                <>
                                    <Box>
                                        <Text fontSize="lg" fontWeight="bold" mb={3}>פרטי בנק</Text>
                                        <SimpleGrid columns={1} spacing={3}>
                                            {applicationDetails?.bankReference && (
                                                <Box>
                                                    <Text fontWeight="semibold">מספר אסמכתא בנק:</Text>
                                                    <Text>{applicationDetails.bankReference}</Text>
                                                </Box>
                                            )}
                                            {applicationDetails?.bankActivityReference && (
                                                <Box>
                                                    <Text fontWeight="semibold">מספר פעילות בנק:</Text>
                                                    <Text>{applicationDetails.bankActivityReference}</Text>
                                                </Box>
                                            )}
                                        </SimpleGrid>
                                    </Box>
                                    <Divider />
                                </>
                            )}

                            {/* User Added Info */}
                            {applicationDetails?.userAddedInfo && (
                                <>
                                    <Box>
                                        <Text fontSize="lg" fontWeight="bold" mb={3}>מידע נוסף מהמשתמש</Text>
                                        <Text>{applicationDetails.userAddedInfo}</Text>
                                    </Box>
                                    <Divider />
                                </>
                            )}

                            {/* Admin Notes */}
                            <Box>
                                <Text fontSize="lg" fontWeight="bold" mb={3}>הערות מנהל</Text>

                                {/* For non-editable users: show notes as read-only */}
                                {!isEditable && originalNotes && (
                                    <Box
                                        p={3}
                                        bg="gray.50"
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="gray.200"
                                    >
                                        <Text>{originalNotes}</Text>
                                    </Box>
                                )}

                                {/* For non-editable users: show message if no notes */}
                                {!isEditable && !originalNotes && (
                                    <Text color="gray.500">אין הערות</Text>
                                )}

                                {/* For editable users: show existing notes in view mode */}
                                {isEditable && originalNotes && !isEditingNotes && (
                                    <Box>
                                        <Box
                                            p={3}
                                            bg="gray.50"
                                            borderRadius="md"
                                            border="1px solid"
                                            borderColor="gray.200"
                                            mb={3}
                                        >
                                            <Text>{originalNotes}</Text>
                                        </Box>
                                        <Button
                                            colorScheme="blue"
                                            onClick={handleEditNotes}
                                            size="sm"
                                            variant="outline"
                                        >
                                            ערוך הערה
                                        </Button>
                                    </Box>
                                )}

                                {/* For editable users: show textarea for new notes or editing existing ones */}
                                {isEditable && (!originalNotes || isEditingNotes) && (
                                    <Box>
                                        <Textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            placeholder="הזן הערה על ההעברה..."
                                            rows={4}
                                            mb={3}
                                        />
                                        <Flex gap={2}>
                                            <Button
                                                colorScheme="blue"
                                                onClick={handleSaveNotes}
                                                size="sm"
                                            >
                                                שמור הערה
                                            </Button>
                                            {originalNotes && (
                                                <Button
                                                    colorScheme="gray"
                                                    onClick={handleCancelEdit}
                                                    size="sm"
                                                    variant="outline"
                                                >
                                                    ביטול
                                                </Button>
                                            )}
                                        </Flex>
                                    </Box>
                                )}
                            </Box>
                            <Divider />

                            {/* Promotions */}
                            {applicationDetails?.promotions && applicationDetails.promotions.length > 0 && (
                                <>
                                    <Box>
                                        <Text fontSize="lg" fontWeight="bold" mb={3}>מבצעים</Text>
                                        <VStack align="stretch" spacing={3}>
                                            {applicationDetails.promotions.map((promotion, index) => (
                                                <Box key={index} p={3} border="1px solid" borderColor="gray.200" borderRadius="md">
                                                    {Object.entries(promotion).map(([key, value]) => (
                                                        <HStack key={key} justify="space-between">
                                                            <Text fontWeight="semibold">{key}:</Text>
                                                            <Text>{value}</Text>
                                                        </HStack>
                                                    ))}
                                                </Box>
                                            ))}
                                        </VStack>
                                    </Box>
                                    <Divider />
                                </>
                            )}

                            {/* Transfer Log */}
                            {transferLog && transferLog.length > 0 && (
                                <Box>
                                    <Text fontSize="lg" fontWeight="bold" mb={3}>היסטוריית העברה</Text>
                                    <Box maxHeight="300px" overflowY="auto" pr={2}>
                                        <VStack align="stretch" spacing={3}>
                                            {transferLog.map((log, index) => (
                                                <Box key={index} p={3} border="1px solid" borderColor="gray.200" borderRadius="md">
                                                    <HStack justify="space-between">
                                                        <Text fontWeight="semibold">{formatTransferLogAction(log.action)}</Text>
                                                        <Text fontSize="sm" color="gray.600">
                                                            {prettifyTransferDate(log.createdDate)}
                                                        </Text>
                                                    </HStack>
                                                </Box>
                                            ))}
                                        </VStack>
                                    </Box>
                                </Box>
                            )}
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter>
                    {/* Only show "Save Changes" button for editable users */}
                    {isEditable && (
                        <Button
                            colorScheme="blue"
                            mr={3}
                            onClick={handleUpdateStatus}
                            isDisabled={
                                !applicationStatus ||
                                applicationStatus === applicationDetails?.status?.toString() ||
                                (showBankInputs && (!bankReference.trim() || !bankActivityReference.trim()))
                            }
                        >
                            שמור שינויים
                        </Button>
                    )}
                    <Button onClick={onClose}>סגור</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
